import classNames from "classnames";
import moment from "moment";
import { InlineCalendar } from "PFComponents/calendar/inline_calendar";
import { Flex, Inline, Stack } from "PFComponents/containers";
import { Icon } from "PFComponents/icon";
import { Tip } from "PFComponents/text/input_field_set/components/tip";
import { Typography } from "PFComponents/typography";
import { utc } from "PFCore/helpers/date";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { AvailabilityMode } from "PFCore/types";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";

import { END_ERROR_KEY, RANGES_ERROR_KEY, START_ERROR_KEY } from "../constants";
import { getFieldError } from "../errors_helper";
import css from "./availability_range_calendar.module.scss";

const MONDAY = 1;
const SUNDAY = 7;

export type AvailabilityRangeCalendarProps = {
  start?: string;
  end?: string;
  hideTitle?: boolean;
  onChange: (start: string | null, end: string | null) => void;
  mode?: AvailabilityMode;
  maxDate?: string;
  matchMaxDate?: string;
  errors?: Record<string, any>;
  wrapped?: boolean;
  minDate?: {
    start?: string;
    end?: string;
  };
  portalRef?;
  isDisabled?: boolean;
};

export type AvailabilityRangeCalendarRef = {
  clear: () => void;
};

export const AvailabilityRangeCalendar = forwardRef<
  AvailabilityRangeCalendarRef,
  AvailabilityRangeCalendarProps
>((props, ref) => {
  const { t } = useTranslation("core");

  const { mode, minDate, maxDate, matchMaxDate, onChange, errors, wrapped } = props;
  const { formatDate, formatISODate } = useDateFormatter();

  const [start, setStart] = useState(props.start);
  const [end, setEnd] = useState(props.end);
  const [hoverEndDay, setHoverEndDay] = useState<any>(null);
  const [hoverStartDay, setHoverStartDay] = useState<any>(null);
  const [showDisclaimer, setShowDisclaimer] = useState<boolean | null>(null);

  useImperativeHandle(ref, () => ({
    clear: () => {
      setStart(undefined);
      setEnd(undefined);
    }
  }));

  const isAfterMatchMonths = (date) => date && matchMaxDate && date.isAfter(utc(matchMaxDate));
  useEffect(() => {
    setShowDisclaimer(isAfterMatchMonths(hoverStartDay) || isAfterMatchMonths(hoverEndDay));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoverStartDay, hoverEndDay]);

  const getStartForMode = () => {
    const startTime = moment(start);

    if (mode === "per_week") {
      if (startTime.isoWeekday() === MONDAY) {
        return startTime;
      }

      const startDateForMode = startTime.clone().startOf("week");
      return startDateForMode.isBefore(utc(minDate?.start)) ? utc(minDate?.start) : startDateForMode;
    }

    return startTime;
  };

  const getEndForMode = () => {
    const endTime = moment(end);

    if (mode === "per_week") {
      if (endTime.isoWeekday() === SUNDAY) {
        return endTime;
      }

      const endDateForMode = endTime.clone().endOf("week");
      return endDateForMode.isAfter(utc(maxDate)) ? utc(maxDate) : endDateForMode;
    }

    return endTime;
  };

  useEffect(() => {
    if (!!start || !!end) {
      onChange?.(
        start ? formatISODate(getStartForMode()) : null,
        end ? formatISODate(getEndForMode()) : null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end, mode]);

  const checkHighlighted = (date) => {
    if (!start || !end) {
      return false;
    }

    let effectiveStart = start;
    let effectiveEnd = end;

    if (mode === "per_week") {
      effectiveStart = formatISODate(getStartForMode());
      effectiveEnd = formatISODate(getEndForMode());
    }

    const fmt = formatISODate(date);
    return effectiveStart <= fmt && fmt <= effectiveEnd;
  };

  const checkHoverStart = (date) => {
    if (end && hoverStartDay) {
      const fmt = formatISODate(date);
      return formatISODate(hoverStartDay) <= fmt && fmt <= formatISODate(end);
    }

    return false;
  };

  const checkHoverEnd = (date) => {
    if (start && hoverEndDay) {
      const fmt = formatISODate(date);
      return formatISODate(start) <= fmt && fmt <= formatISODate(hoverEndDay);
    }

    return false;
  };

  const footerMessage =
    showDisclaimer && matchMaxDate ? (
      <Inline>
        <Icon name="info" size="sm" color="paletteOrange0" />
        {t("availabilityDisclaimer", { maxDate: matchMaxDate })}
      </Inline>
    ) : undefined;

  const generalError = errors?.[RANGES_ERROR_KEY];

  return (
    <Stack>
      {!props.hideTitle && <Typography variant="labelRegular">{t("timePeriod")}</Typography>}
      <Flex className={classNames(css.root, { [css.wrap]: wrapped })}>
        <InlineCalendar
          label={props.hideTitle ? t("startDate") : undefined}
          placeholder={t("startDate")}
          style={{ width: 160 }}
          qaId="calendar-start-date"
          selectedDate={props.start ? formatISODate(props.start) : null}
          displayValue={start ? formatDate(getStartForMode()) : undefined}
          minDate={props?.minDate?.start || formatISODate()}
          maxDate={end ? formatISODate(end) : maxDate}
          handleChange={(value) => {
            setStart(formatISODate(value));
          }}
          error={getFieldError(errors || {}, START_ERROR_KEY)}
          calendarProps={{
            handleMouseEnter: (day) => setHoverStartDay(day),
            handleMouseLeave: () => setHoverStartDay(null),
            checkHighlighted,
            checkHover: checkHoverStart,
            footerMessage,
            matchMaxDate
          }}
          floatingOptions={{
            placement: "bottom-start"
          }}
          portalRef={props.portalRef}
          disabled={props.isDisabled}
        />
        <InlineCalendar
          label={props.hideTitle ? t("endDate") : undefined}
          placeholder={t("endDate")}
          style={{ width: 160 }}
          qaId="calendar-end-date"
          selectedDate={props.end ? formatISODate(props.end) : null}
          displayValue={end ? formatDate(getEndForMode()) : undefined}
          minDate={start ? formatISODate(start) : props?.minDate?.end}
          maxDate={maxDate}
          handleChange={(value) => setEnd(value ?? undefined)}
          error={getFieldError(errors || {}, END_ERROR_KEY)}
          calendarProps={{
            handleMouseEnter: (day) => setHoverEndDay(day),
            handleMouseLeave: () => setHoverEndDay(null),
            checkHover: checkHoverEnd,
            checkHighlighted,
            footerMessage,
            matchMaxDate
          }}
          floatingOptions={{
            placement: "bottom-start"
          }}
          portalRef={props.portalRef}
          disabled={props.isDisabled}
        />
      </Flex>
      {generalError && <Tip error={generalError} />}
    </Stack>
  );
});

AvailabilityRangeCalendar.displayName = "AvailabilityRangeCalendar";
